/**
 * Role-based sidebar configuration.
 * Used to filter which dashboard tabs each role sees.
 *
 * - role: matches user.role (from /me API role1)
 * - tabs: route titles always visible for this role (must match route.title in routes/index.js)
 * - conditionalTabs: { tab, when: (user) => boolean } for tabs that depend on user state (e.g. variant)
 *
 * Roles not listed here use default behavior: all dashboard tabs,
 * with Inventory (Pro variant only) and Analytics (Owner only).
 *
 * To add a new role: push an object { role, tabs, conditionalTabs? } to ROLE_SIDEBAR_CONFIG.
 */

export const ROLE_SIDEBAR_CONFIG = [
  {
    role: "Pharmacy",
    tabs: ["Prescriptions", "Payments", "Settings"],
    conditionalTabs: [
      {
        tab: "Inventory",
        when: (user) => !!user?.variant?.startsWith("Pro"),
      },
    ],
  },
  // Example: add more roles as needed
  // {
  //   role: "Admin",
  //   tabs: ["Appointments", "Treatments", "Prescriptions", "Payments", "Settings"],
  //   conditionalTabs: [
  //     { tab: "Inventory", when: (user) => !!user?.variant?.startsWith("Pro") },
  //     { tab: "Analytics", when: (user) => user?.role?.startsWith("Owner") },
  //   ],
  // },
];

/**
 * Get config for a role, or null if role uses default behavior.
 */
export const getRoleSidebarConfig = (role) =>
  ROLE_SIDEBAR_CONFIG.find((c) => c.role === role) ?? null;

/**
 * Check if a route title is allowed for the current user based on role config.
 * Returns true if allowed, false otherwise.
 * If no config for role, returns null (caller should use default logic).
 */
export const isTabAllowedForRole = (routeTitle, user, config) => {
  if (!config) return null;

  if (config.tabs.includes(routeTitle)) return true;

  const conditional = config.conditionalTabs?.find(
    (ct) => ct.tab === routeTitle,
  );
  if (conditional) return !!conditional.when?.(user);

  return false;
};
